import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { Edit, MoreHorizontal, Trash2 } from 'lucide-react'
import { ReactNode, useState } from 'react'

import type { FC } from 'react'

// Design tokens for consistent card styling
const CARD_STYLES = {
	// Default state - clean and minimal
	default: {
		border: 'border border-border/50',
		shadow: 'shadow-sm',
		background: 'bg-card',
	},
	// Hover state - subtle elevation and feedback
	hover: {
		border: 'hover:border-border',
		shadow: 'hover:shadow-md',
		transform: 'hover:-translate-y-[1px]',
	},
	// Selected/Active state - clear primary indication
	selected: {
		border: 'border-2 border-primary',
		shadow: 'shadow-md',
	},
	// Selected + Hover state - emphasized but refined
	selectedHover: {
		border: 'hover:border-primary hover:border-2',
		shadow: 'hover:shadow-lg',
		transform: 'hover:-translate-y-[1px]',
		glow: 'hover:shadow-primary/10',
	},
	// Focus state
	focus: {
		ring: 'focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:outline-none',
	},
	// Transitions
	transition: 'transition-all duration-200 ease-in-out',
	// Action button area - ensures consistent height and centering
	actionArea: 'min-h-[32px] flex items-center justify-center',
	// Header row - consistent height with vertically centered content
	headerRow: 'min-h-[32px] flex items-center justify-between gap-3',
} as const

export interface BaseCardAction {
	id: string
	label: string
	icon: ReactNode
	onClick: () => void
	variant?: 'default' | 'destructive'
}

export interface BaseCardProps {
	children?: ReactNode
	className?: string
	isSelected?: boolean
	isClickable?: boolean
	onClick?: () => void
	onMouseEnter?: () => void
	onMouseLeave?: () => void
	
	// Header configuration
	title?: string
	titleIcon?: ReactNode
	
	// Action button configuration
	actions?: BaseCardAction[]
	showActionsOnHover?: boolean
	singleActionIcon?: ReactNode
	singleActionOnClick?: () => void
	singleActionVariant?: 'default' | 'destructive'
	
	// Accessibility
	'aria-label'?: string
	'aria-selected'?: boolean
	role?: string
	tabIndex?: number
}

export const BaseCard: FC<BaseCardProps> = ({
	children,
	className,
	isSelected = false,
	isClickable = false,
	onClick,
	onMouseEnter,
	onMouseLeave,
	title,
	titleIcon,
	actions,
	showActionsOnHover = true,
	singleActionIcon,
	singleActionOnClick,
	singleActionVariant = 'default',
	'aria-label': ariaLabel,
	'aria-selected': ariaSelected,
	role,
	tabIndex,
}) => {
	const [isHovered, setIsHovered] = useState(false)

	const handleMouseEnter = () => {
		setIsHovered(true)
		onMouseEnter?.()
	}

	const handleMouseLeave = () => {
		setIsHovered(false)
		onMouseLeave?.()
	}

	const handleCardClick = () => {
		if (isClickable && onClick) {
			onClick()
		}
	}

	const handleActionClick = (e: React.MouseEvent, actionOnClick: () => void) => {
		e.stopPropagation()
		actionOnClick()
	}

	// Determine if actions should be visible
	const shouldShowActions = showActionsOnHover ? isHovered : true
	const hasActions = (actions && actions.length > 0) || singleActionOnClick

	// Build card classes
	const cardClasses = cn(
		// Base styles
		CARD_STYLES.default.border,
		CARD_STYLES.default.shadow,
		CARD_STYLES.default.background,
		CARD_STYLES.transition,
		
		// Interactive styles
		isClickable && [
			'cursor-pointer',
			CARD_STYLES.focus.ring,
		],
		
		// Hover styles (different for selected vs unselected)
		isClickable && !isSelected && [
			CARD_STYLES.hover.border,
			CARD_STYLES.hover.shadow,
			CARD_STYLES.hover.transform,
		],
		
		// Selected state
		isSelected && [
			CARD_STYLES.selected.border,
			CARD_STYLES.selected.shadow,
		],
		
		// Selected + Hover (more emphasized)
		isClickable && isSelected && [
			CARD_STYLES.selectedHover.border,
			CARD_STYLES.selectedHover.shadow,
			CARD_STYLES.selectedHover.transform,
			CARD_STYLES.selectedHover.glow,
		],
		
		// Remove default card padding since we control it in CardContent
		'py-0',
		
		className
	)

	// Single action button (like trash icon)
	const renderSingleAction = () => (
		<div className={cn(CARD_STYLES.actionArea, shouldShowActions ? 'opacity-100' : 'opacity-0')}>
			<Button
				variant={singleActionVariant === 'destructive' ? 'ghost' : 'ghost'}
				size="icon"
				className={cn(
					'h-8 w-8 transition-opacity duration-200',
					singleActionVariant === 'destructive' && 'text-destructive hover:text-destructive hover:bg-destructive/10'
				)}
				onClick={(e) => handleActionClick(e, singleActionOnClick!)}
				tabIndex={isClickable ? -1 : 0}
			>
				{singleActionIcon}
			</Button>
		</div>
	)

	// Multiple actions dropdown
	const renderMultipleActions = () => (
		<div className={cn(CARD_STYLES.actionArea, shouldShowActions ? 'opacity-100' : 'opacity-0')}>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 transition-opacity duration-200"
						onClick={(e) => e.stopPropagation()}
						tabIndex={isClickable ? -1 : 0}
					>
						<MoreHorizontal className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					{actions?.map((action) => (
						<DropdownMenuItem
							key={action.id}
							onClick={(e) => handleActionClick(e, action.onClick)}
							className={cn(
								action.variant === 'destructive' && 'text-destructive focus:text-destructive focus:bg-destructive/10'
							)}
						>
							<span className={cn(
								"mr-2 h-4 w-4 flex items-center justify-center",
								action.variant === 'destructive' ? 'text-destructive' : 'text-foreground'
							)}>
								{action.icon}
							</span>
							<span>{action.label}</span>
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	)

	return (
		<Card
			className={cardClasses}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={handleCardClick}
			aria-label={ariaLabel}
			aria-selected={ariaSelected}
			role={role}
			tabIndex={isClickable ? (tabIndex ?? 0) : undefined}
		>
			<CardContent className="px-4 py-3">
				{/* Header row - title/icon and actions */}
				<div className={cn(
					CARD_STYLES.headerRow,
					(title || titleIcon || hasActions) ? 'mb-2' : ''
				)}>
					<div className="flex-1 min-w-0 flex items-center gap-2">
						{titleIcon && (
							<div className="flex-shrink-0">
								{titleIcon}
							</div>
						)}
						{title && (
							<h3 className="text-base font-medium truncate">{title}</h3>
						)}
					</div>
					{hasActions && (
						<div className="flex-shrink-0">
							{singleActionOnClick 
								? renderSingleAction()
								: actions && actions.length > 0 
									? renderMultipleActions()
									: null
							}
						</div>
					)}
				</div>
				
				{/* Content area - below header row */}
				{children && (
					<div>
						{children}
					</div>
				)}
			</CardContent>
		</Card>
	)
}

// Utility hook for common card action patterns
export const useCardActions = () => {
	const createEditAction = (onEdit: () => void): BaseCardAction => ({
		id: 'edit',
		label: 'Edit',
		icon: <Edit className="h-4 w-4" />,
		onClick: onEdit,
	})

	const createRemoveAction = (onRemove: () => void): BaseCardAction => ({
		id: 'remove',
		label: 'Remove',
		icon: <Trash2 className="h-4 w-4" />,
		onClick: onRemove,
		variant: 'destructive',
	})

	const createDeleteAction = (onDelete: () => void): BaseCardAction => ({
		id: 'delete',
		label: 'Delete',
		icon: <Trash2 className="h-4 w-4" />,
		onClick: onDelete,
		variant: 'destructive',
	})

	return {
		createEditAction,
		createRemoveAction,
		createDeleteAction,
	}
}

